import { useOwnershipLabels } from "../../OwnershipLabels";
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CircleFlag } from 'react-circle-flags';
import Select from 'react-select';
import CountrySelect from '../../../components/ui/CountrySelect';
import Modal from '../../../components/ui/Modal';
import { useLockBodyScroll } from '../common/common_utils/useLockBodyScroll';
import { BottomSheet } from './KycBottomSheet';
import { CLIENT_ENTITY_TYPES, HOLDING_KINDS, TRUST_KINDS, entityTypeComplete, entityTypeLabel } from '../../clientEntityType';
import { EntityTypeOptions } from './KycEntityTypeChoice';
import countryList from 'react-select-country-list';
import { countryMatchesSearch } from '../../../utils/iso2to3';
import { MOBILE_TEXT_ENTRY_PROPS } from '../mobile/mobileTextEntryProps';
import '../mobile/KycMobileClientSections.css';

const countryNames = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; }
})();
const countryName = iso2 => {
  try { return countryNames?.of(iso2) || iso2; } catch { return iso2; }
};
// Documents of one case of the requested types (org setting entitiesDocTypes; the API already sends
// only those), required first, then optional ones.
function caseDocuments(kase, docTypes) {
  const list = (kase?.docs || []).filter(doc => doc.label && docTypes.includes(doc.type || 'registration'));
  return [...list.filter(doc => !doc.optional), ...list.filter(doc => doc.optional)];
}

function uniqueDocuments(docs) {
  const seen = new Set();
  return docs.filter(doc => {
    const id = doc.label.toLowerCase();
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

// What the guide shows for the selected company type (index), or for all types (null / "other").
export function entityDocumentsGuide(corporateDocuments, selected = null, docTypes = ['registration']) {
  const cases = (corporateDocuments?.cases || []).map(kase => ({ ...kase, docs: caseDocuments(kase, docTypes) })).filter(kase => kase.docs.length);
  // A country with one company type reads as that type.
  const kase = Number.isInteger(selected) ? cases[selected] : cases.length === 1 ? cases[0] : null;
  if (kase) {
    const required = kase.docs.filter(doc => !doc.optional);
    return {
      cases,
      docs: kase.docs,
      required,
      optional: kase.docs.filter(doc => doc.optional),
      rule: kase.logic === 'ONE_OF' ? 'Any one of these' : required.length > 1 ? 'Usually requested together' : 'Usually requested',
      // Several documents to provide together (not one of them).
      together: kase.logic !== 'ONE_OF' && required.length > 1,
    };
  }
  // All company types: a document is optional only when it is optional in every type that lists it.
  const docs = uniqueDocuments(cases.flatMap(item => item.docs))
    .map(doc => ({ ...doc, optional: cases.every(item => item.docs.every(d => d.label !== doc.label || d.optional)) }));
  return {
    cases,
    docs,
    required: docs.filter(doc => !doc.optional),
    optional: docs.filter(doc => doc.optional),
    rule: 'Usually accepted',
    together: false,
  };
}

export const OPTIONAL_DOCUMENTS_HINT = "Add these if the main document doesn't show the company's directors or shareholders. It helps us review faster.";

// A document recognised among the uploads shows a tick instead of the document icon.
function DocumentRows({ docs, compact, ticked = [] }) {
  return <ul className={compact ? 'is-compact' : undefined}>{docs.map(doc => <li key={doc.label} className={`kycEntityGuideDoc${ticked.includes(doc.label) ? ' is-uploaded' : ''}`}>
    <i className={ticked.includes(doc.label) ? 'bi bi-check-circle-fill' : 'bi bi-file-earmark-text'} aria-label={ticked.includes(doc.label) ? 'Uploaded' : undefined} aria-hidden={ticked.includes(doc.label) ? undefined : 'true'} />
    <span className="kycEntityGuideDocName">
      {doc.label}
      {doc.secondaryLabel && <small>{doc.secondaryLabel}</small>}
    </span>
  </li>)}</ul>;
}

const EASE = [0.22, 1, 0.36, 1];

// General guidance, used when no company type is chosen or the country has no list.
const GENERAL_EXAMPLES = {
  registration: 'a certificate of incorporation, commercial register extract or trade licence',
  good_standing: 'a certificate of good standing',
  constitutional: 'the memorandum or articles of association',
  ownership: 'the register of shareholders and directors',
};
export function entityGeneralGuidance(docTypes = ['registration']) {
  const examples = docTypes.map(type => GENERAL_EXAMPLES[type]).filter(Boolean);
  if (docTypes.length === 1 && docTypes[0] === 'registration') {
    return `Upload your company's official registration document, such as ${examples[0]}.`;
  }
  const list = examples.length > 1 ? `${examples.slice(0, -1).join('; ')}; and ${examples[examples.length - 1]}` : examples[0];
  return `Upload your company's official documents: ${list}.`;
}

// Animates its height to follow its content (type chosen, country changed, list loaded).
function AnimatedHeight({ children }) {
  const reduceMotion = useReducedMotion();
  const inner = useRef(null);
  const [height, setHeight] = useState('auto');
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <motion.div className="kycEntityGuideAnimated" initial={false} animate={{ height }}
    transition={{ duration: reduceMotion ? 0 : 0.38, ease: EASE }}>
    <div ref={inner} className="kycEntityGuideAnimatedInner">{children}</div>
  </motion.div>;
}

// Content swap: crossfade only (the old content fades out over the new one, nothing moves);
// the exiting content is taken out of the flow so the height follows the new content.
function Swap({ id, children }) {
  const reduceMotion = useReducedMotion();
  const fade = { duration: reduceMotion ? 0 : 0.22, ease: 'easeInOut' };
  return <AnimatePresence mode="popLayout" initial={false}>
    <motion.div key={id} className="kycEntityGuideSwap"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1, transition: fade }}
      exit={reduceMotion ? undefined : { opacity: 0, transition: fade }}>
      {children}
    </motion.div>
  </AnimatePresence>;
}

// "Change": organisation type and country of registration together, applied on Save (desktop modal, mobile bottom sheet).
const sameEntityType = (a = {}, b = {}) => a.type === b.type && (a.holdingKind || '') === (b.holdingKind || '') && (a.trustKind || '') === (b.trustKind || '');
const ENTITY_TYPE_OPTIONS = CLIENT_ENTITY_TYPES.map(option => ({ value: option.value, label: option.title, iconSrc: option.iconSrc }));
const FOLLOW_UP_NAMES = { holdingKind: { active: 'Active', passive: 'Passive' }, trustKind: { trust: '', trustee: "Trustee's name" } };
const followUpKey = type => (type === 'holding' ? 'holdingKind' : type === 'trust' ? 'trustKind' : '');

// Mobile country picker: same sheet as the country fields of step 2 (KycMobileCustomerDetails), with the
// header of the organisation type modal.
const COUNTRY_OPTIONS = countryList().getData();
function MobileCountrySheet({ open, value, title, onClose, onChoose }) {
  const [search, setSearch] = useState('');
  useEffect(() => { if (open) setSearch(''); }, [open]);
  const options = COUNTRY_OPTIONS.filter(option => countryMatchesSearch(option, search));
  return createPortal(<AnimatePresence>
    {open && <>
      <motion.div key="backdrop" className="kycMobileSheetBackdrop kycOrgCountryBackdrop" onClick={onClose}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
      <motion.div key="sheet" className="kycMobileCountrySheet kycOrgCountrySheet" role="dialog" aria-modal="true" aria-label={title}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.18 }}>
        {/* Header as the organisation type modal: title, then ✕. */}
        <div className="uiModalHeader">
          <div className="uiModalTitle">{title}</div>
          <button type="button" className="uiModalClose" aria-label="Close" title="Close" onClick={onClose}>✕</button>
        </div>
        <div className="kycMobileCountrySearchWrap">
          <i className="bi bi-search" aria-hidden="true" />
          <input {...MOBILE_TEXT_ENTRY_PROPS} autoFocus className="kycMobileCountrySearchInput" value={search}
            onChange={event => setSearch(event.target.value)} placeholder="Search country" aria-label="Search country"
            onKeyDown={event => { if (event.key === 'Enter' && options.length === 1) { event.preventDefault(); onChoose(options[0].value); } }} />
        </div>
        <div className="kycMobileCountryList">
          {options.map(option => {
            const selected = option.value === value;
            return <button key={option.value} type="button" className={`kycMobileCountryOption ${selected ? 'is-selected' : ''}`}
              onClick={() => onChoose(option.value)}>
              <span className="kycMobileCountryOptionLeft">
                <CircleFlag className="kycMobilePickerFlag" countryCode={option.value.toLowerCase()} height="18" />
                <span>{option.label}</span>
              </span>
              <i className={`bi ${selected ? 'bi-check2' : 'bi-chevron-right'}`} aria-hidden="true" />
            </button>;
          })}
        </div>
      </motion.div>
    </>}
  </AnimatePresence>, document.body);
}

// Mobile: a short sheet with two large buttons; each opens a picker (type cards in a centred modal, or the country sheet).
// A choice is final: it applies and closes the picker and the sheet.
function MobileOrganisationSheet({ open, entityType, country, onClose, onSave }) {
  const { ownershipLabel } = useOwnershipLabels();
  const [picking, setPicking] = useState('');
  useEffect(() => { if (!open) setPicking(''); }, [open]);
  const apply = next => { onSave({ entityType, country, ...next }); setPicking(''); onClose(); };
  const typeOption = CLIENT_ENTITY_TYPES.find(option => option.value === entityType?.type);
  const typeName = entityType?.type ? `${typeOption?.title || ''}${entityType.type === 'holding' && entityType.holdingKind ? ` · ${FOLLOW_UP_NAMES.holdingKind[entityType.holdingKind]}` : ''}${entityType.type === 'trust' && FOLLOW_UP_NAMES.trustKind[entityType.trustKind] ? ` · ${FOLLOW_UP_NAMES.trustKind[entityType.trustKind]}` : ''}` : 'Choose';
  return <>
    <BottomSheet open={open} title="Your organisation" onClose={onClose} className="kycOrgSheet">
      <div className="kycOrgPicks">
        <button type="button" className="kycOrgPick" aria-label="Change organisation type" onClick={() => setPicking('type')}>
          <span className="kycOrgPickIcon" aria-hidden="true">{typeOption && <img src={typeOption.iconSrc} alt="" />}</span>
          <span className="kycOrgPickText"><small>Organisation type</small>{ownershipLabel(typeName)}</span>
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
        <button type="button" className="kycOrgPick" aria-label="Change country of registration" onClick={() => setPicking('country')}>
          <span className="kycOrgPickIcon" aria-hidden="true">{/^[A-Z]{2}$/.test(country || '') && <CircleFlag countryCode={country.toLowerCase()} height="26" />}</span>
          <span className="kycOrgPickText"><small>Country of registration</small>{country ? countryName(country) : 'Choose'}</span>
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </BottomSheet>
    {createPortal(<Modal open={picking === 'type'} title="Organisation type"
      className="kycEntityCountryModal kycOrgPickerModal" onClose={() => setPicking('')}>
      {/* A fresh choice: nothing pre-selected. */}
      {picking === 'type' && <EntityTypeOptions commonBadge lockScroll={false} value={{ type: '', holdingKind: '', trustKind: '' }} onChange={next => apply({ entityType: next })} />}
    </Modal>, document.body)}
    <MobileCountrySheet open={picking === 'country'} value={country} title="Country of registration"
      onClose={() => setPicking('')} onChoose={value => apply({ country: value })} />
  </>;
}

function ChangeOrganisationModal({ open, entityType, country, onClose, onSave }) {
  const { ownershipLabel } = useOwnershipLabels();
  const entityTypeOptions = ENTITY_TYPE_OPTIONS.map(option => ({ ...option, label: ownershipLabel(option.label) }));
  useLockBodyScroll(open);
  const reduceMotion = useReducedMotion();
  const [draftType, setDraftType] = useState(entityType);
  const [draftCountry, setDraftCountry] = useState(country);
  useEffect(() => {
    if (open) { setDraftType(entityType); setDraftCountry(country); }
  }, [open]); // eslint-disable-line
  // Holding / trust: once the follow-up is answered it closes and the field reads e.g. "Holding company · Active"
  // or "Trust · Trustee's name" (named as the trust: just "Trust"); choosing the type again in the list asks it again.
  const answeredKey = followUpKey(draftType.type);
  const typeOptionLabel = (option, { context }) => <span className="kycOrgModalTypeOption">
    <img src={option.iconSrc} alt="" aria-hidden="true" />{option.label}
    {context === 'value' && option.value === draftType.type && FOLLOW_UP_NAMES[answeredKey]?.[draftType[answeredKey]] && ` · ${FOLLOW_UP_NAMES[answeredKey][draftType[answeredKey]]}`}
  </span>;
  const followUp = answeredKey && draftType[answeredKey] ? null
    : draftType.type === 'holding' ? { title: 'Holding type', options: HOLDING_KINDS, key: 'holdingKind' }
    : draftType.type === 'trust' ? { title: 'Client name', options: TRUST_KINDS, key: 'trustKind' } : null;
  const changed = !sameEntityType(draftType, entityType) || draftCountry !== country;
  const canSave = changed && entityTypeComplete(draftType) && /^[A-Z]{2}$/.test(draftCountry || '');
  const chooseType = setDraftType;
  const chooseCountry = value => setDraftCountry(value || '');
  const footer = <>
    <button type="button" className="btn btn-modal-cancel" onClick={onClose}>Cancel</button>
    <button type="button" className="btn btn-modal-save" disabled={!canSave}
      onClick={() => { onSave({ entityType: draftType, country: draftCountry }); onClose(); }}>Save</button>
  </>;
  const card = open && <div className="kycOrgModalCard">
      <div className="kycOrgModalField">
        <label htmlFor="kycOrgModalType">Organisation type</label>
        <Select inputId="kycOrgModalType" classNamePrefix="rs" className="kycOrgModalSelect" isSearchable={false}
          options={entityTypeOptions} value={entityTypeOptions.find(option => option.value === draftType.type) || null}
          formatOptionLabel={typeOptionLabel} placeholder="Select"
          menuPortalTarget={document.body} menuPosition="fixed" styles={{ menuPortal: base => ({ ...base, zIndex: 5000 }) }}
          onChange={option => chooseType({ type: option?.value || '', holdingKind: '', trustKind: '' })} />
      </div>
      {/* This modal keeps the holding / trust question below the type (no second modal); it slides open and
          closed (the negative margin cancels the card gap while it is collapsed). */}
      <AnimatePresence initial={false}>
      {followUp && <motion.div key={followUp.key} className="kycOrgModalField kycOrgModalFollowUp"
        initial={reduceMotion ? false : { height: 0, opacity: 0, marginTop: -16 }}
        animate={{ height: 'auto', opacity: 1, marginTop: 0 }}
        exit={reduceMotion ? undefined : { height: 0, opacity: 0, marginTop: -16 }}
        transition={{ duration: reduceMotion ? 0 : 0.28, ease: EASE }}>
        <p className="kycOrgModalLabel">{followUp.title}</p>
        <div className="kycEntityTypeList is-compact" role="radiogroup" aria-label={followUp.title}>
          {followUp.options.map(option => <button key={option.value} type="button" role="radio" aria-label={option.label}
            aria-checked={draftType[followUp.key] === option.value}
            className={`kycEntityTypeOption${draftType[followUp.key] === option.value ? ' is-selected' : ''}`}
            onClick={() => chooseType({ ...draftType, [followUp.key]: option.value })}>
            <span><strong>{option.label}</strong><small>{option.hint}</small></span>
          </button>)}
        </div>
      </motion.div>}
      </AnimatePresence>
      <div className="kycOrgModalField">
        <label htmlFor="kycOrgModalCountry">Country of registration</label>
        <CountrySelect inputId="kycOrgModalCountry" value={draftCountry || ''} placeholder="Search a country"
          onChange={chooseCountry} />
      </div>
    </div>;
  return createPortal(<Modal open={open} title="Your organisation" className="kycEntityCountryModal kycOrgModal" onClose={onClose} footer={footer}>
    {card}
  </Modal>, document.body);
}

// Mobile checklist: circles that tick when a document is recognised, only for a short list of one
// company type (or a country with a single type); otherwise plain rows.
const CHECKLIST_MAX = 5;
function MobileDocumentRows({ docs, checklist, ticked = [] }) {
  return <ul className="kycEntityMobileDocs">{docs.map(doc => {
    const done = checklist && ticked.includes(doc.label);
    return <li key={doc.label} className="kycEntityMobileDoc">
      {checklist
        ? <span className={`kycEntityMobileCheck${done ? ' is-done' : ''}`} aria-label={done ? 'Uploaded' : 'Not uploaded yet'}>{done && <i className="bi bi-check-lg" aria-hidden="true" />}</span>
        : <i className="bi bi-file-earmark-text" aria-hidden="true" />}
      <span className="kycEntityMobileDocName">{doc.label}{doc.secondaryLabel && <small>{doc.secondaryLabel}</small>}</span>
    </li>;
  })}</ul>;
}

// Left panel of the company "How would you like to start?" card: country of registration, optional
// company type and the documents usually accepted (requested, then optional). Guidance only: uploading never
// depends on the selection, and staff review whatever is uploaded.
// Mobile (kyc-sent-mobile): "1 Your organisation" and "2 Required documents" sections, pickers in bottom sheets.
export default function KycEntityDocumentsGuide({ country, corporateDocuments, onCountryChange, disabled = false, docTypes = ['registration'], mobile = false, ticked = [], documentsBadge = null,
  companyType = '', onCompanyTypeChange = () => {},
  // Company types the recognised documents point to (no type chosen yet): highlighted as a hint and the list
  // narrowed to their documents. Display only; the client's choice is not changed.
  suggestedTypes = [],
  // Client entity type question (top of "Your company"); documents only for the types that upload first.
  entityTypeSlot = null, showDocuments = true, documentsNote = '',
  // Mobile: an upload button at the bottom of the card (before any file is added).
  uploadSlot = null,
  // Once the type and country are known: one "Change" for both (entityType + onEntityTypeChange).
  entityType = null, onEntityTypeChange = () => {} }) {
  const { ownershipLabel } = useOwnershipLabels();
  const countryId = useId();
  const [changingCountry, setChangingCountry] = useState(false);
  const [choosingType, setChoosingType] = useState(false);
  const [changingOrganisation, setChangingOrganisation] = useState(false);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const suggestedKey = suggestedTypes.join('|');
  useEffect(() => { setShowAllTypes(false); }, [suggestedKey]);
  const hasCountry = /^[A-Z]{2}$/.test(country || '');
  useEffect(() => { setOptionalOpen(false); }, [country]);

  if (!hasCountry && mobile) {
    return <div className="kycEntityMobileGuide">
      <section className="kycEntityMobileSec is-company">
        <h3 className="kycEntityMobileSecHead"><span className="kycEntityMobileNum">1</span>Your organisation</h3>
        {entityTypeSlot}
        <label className="kycEntityMobileLabel" htmlFor={countryId}>Country of registration</label>
        <CountrySelect inputId={countryId} value="" placeholder="Select the country" disabled={disabled}
          onChange={value => { if (value) onCountryChange(value); }} />
        <p className="kycEntityMobileSmall">The documents usually accepted depend on the country where the company is registered.</p>
      </section>
    </div>;
  }

  if (!hasCountry) {
    return <div className="kycEntityGuide">
      {entityTypeSlot}
      <div className="kycEntityGuideCountryField">
        <label htmlFor={countryId}>Country of registration</label>
        <CountrySelect inputId={countryId} value="" placeholder="Select the country" disabled={disabled}
          onChange={value => { if (value) onCountryChange(value); }} />
        <small>The documents usually accepted depend on the country where the company is registered.</small>
      </div>
    </div>;
  }

  // The list of the country shown in the card; while it loads (or after a country change) nothing stale is shown.
  const forCountry = corporateDocuments?.country === country ? corporateDocuments : null;
  const loading = !forCountry;
  // The company type is kept in the KYC record (customer.companyType): a case name, or "not_listed".
  const baseCases = entityDocumentsGuide(forCountry, null, docTypes).cases;
  const typeIndex = baseCases.findIndex(kase => kase.name && kase.name === companyType);
  const selected = companyType === 'not_listed' ? 'other' : typeIndex >= 0 ? typeIndex : null;
  const setSelected = value => onCompanyTypeChange(value === 'other' ? 'not_listed' : Number.isInteger(value) ? baseCases[value]?.name || '' : '');
  const guide = entityDocumentsGuide(forCountry, selected, docTypes);
  const showTypes = guide.cases.length > 1;
  // Hint from the recognised documents: the matching types (by index), when no type is chosen.
  const hinted = showTypes && selected === null ? baseCases.map((kase, index) => (suggestedTypes.includes(kase.name) ? index : -1)).filter(i => i >= 0) : [];
  const narrowed = hinted.length > 0 && !showAllTypes;
  // What the document list shows: the chosen type, the hinted type(s), or every type.
  const listGuide = !narrowed ? guide
    : hinted.length === 1 ? entityDocumentsGuide(forCountry, hinted[0], docTypes)
    : entityDocumentsGuide({ ...forCountry, cases: (forCountry?.cases || []).filter(kase => suggestedTypes.includes(kase.name)) }, null, docTypes);
  const hintNames = hinted.map(index => baseCases[index]?.name).filter(Boolean);
  const hintLine = hinted.length > 0 && <p className="kycEntityGuideSuggestion">
    <i className="bi bi-stars" aria-hidden="true" />
    <span>{narrowed ? 'Filtered based on your uploaded document' : 'Showing every company type'}</span>
    <button type="button" className="kycEntityGuideSuggestionToggle" onClick={() => setShowAllTypes(value => !value)}>{narrowed ? 'Show all' : 'Show suggested'}</button>
  </p>;
  const general = !loading && !narrowed && (selected === 'other' || (showTypes && selected === null) || forCountry.fallback || !guide.docs.length);
  const listKey = loading ? 'loading' : `${forCountry.fallback ? 'general' : ''}:${selected}:${narrowed ? hinted.join(',') : ''}`;

  // Type icon over both rows, then the type and the country; one Change for both (desktop, and mobile once the type is answered).
  const organisationHead = <>
    <div className="kycEntityGuideHead">
      <img className="kycEntityGuideHeadIcon" src={CLIENT_ENTITY_TYPES.find(option => option.value === entityType?.type)?.iconSrc} alt="" aria-hidden="true" />
      <span className="kycEntityGuideHeadType">
        {ownershipLabel(entityTypeLabel(entityType || {}))}
        {entityType?.type === 'trust' && <small>{TRUST_KINDS.find(kind => kind.value === entityType.trustKind)?.label}</small>}
      </span>
      <Swap id={country}>
        <span className="kycEntityGuideCountryName">
          <span className="kycEntityGuideFlag" aria-hidden="true"><CircleFlag countryCode={country.toLowerCase()} height="18" /></span>
          <span>Registered in <strong>{countryName(country)}</strong></span>
        </span>
      </Swap>
      <button type="button" className="kycEntityGuideLink kycEntityGuideHeadChange" aria-label="Change organisation type or country"
        disabled={disabled} onClick={() => setChangingOrganisation(true)}>Change</button>
    </div>
    {(() => {
      const Editor = mobile ? MobileOrganisationSheet : ChangeOrganisationModal;
      return <Editor open={changingOrganisation} entityType={entityType || {}} country={country}
        onClose={() => setChangingOrganisation(false)}
        onSave={next => {
          if (next.country !== country) onCountryChange(next.country);
          if (!sameEntityType(next.entityType, entityType || {})) onEntityTypeChange(next.entityType);
        }} />;
    })()}
  </>;

  if (mobile) {
    const typeName = selected === 'other' ? 'Not listed / not sure' : Number.isInteger(selected) ? guide.cases[selected]?.name : '';
    const oneType = Number.isInteger(selected) || guide.cases.length === 1 || (narrowed && hinted.length === 1);
    const checklist = oneType && listGuide.required.length <= CHECKLIST_MAX;
    // What to upload, as an instruction (the desktop card keeps the "Usually …" labels).
    const mobileRule = !oneType && showTypes ? 'Upload the one that matches your company:'
      : listGuide.rule === 'Any one of these' ? 'Upload any one of these:'
      : listGuide.required.length > 1 ? 'Upload all of these:' : 'Upload this document:';
    const showList = !loading && !forCountry.fallback;
    return <div className="kycEntityMobileGuide">
      <section className="kycEntityMobileSec is-company">
        <h3 className="kycEntityMobileSecHead"><span className="kycEntityMobileNum">1</span>Your organisation</h3>
        {entityTypeComplete(entityType || {}) ? organisationHead : <>
          {entityTypeSlot}
          <div className="kycEntityMobileCountry">
            <Swap id={country}>
              <span className="kycEntityGuideCountryName">
                <span className="kycEntityGuideFlag" aria-hidden="true"><CircleFlag countryCode={country.toLowerCase()} height="20" /></span>
                <span>Registered in <strong>{countryName(country)}</strong></span>
              </span>
            </Swap>
            <button type="button" className="kycEntityGuideLink" aria-label="Change country" disabled={disabled} onClick={() => setChangingCountry(true)}>Change</button>
          </div>
        </>}
        <AnimatedHeight>
          <Swap id={`${country}:${loading ? 'loading' : 'ready'}`}>
            {showDocuments && showTypes && <div className="kycEntityMobileType">
              <p className="kycEntityMobileLabel">Company type <span>· optional</span></p>
              <button type="button" className={`kycEntityMobileField${typeName ? '' : ' is-placeholder'}`} disabled={disabled}
                aria-haspopup="dialog" onClick={() => setChoosingType(true)}>
                <span>{typeName || (narrowed ? `Suggested: ${hintNames.join(' or ')}` : 'Choose your company type')}</span><i className="bi bi-chevron-down" aria-hidden="true" />
              </button>
            </div>}
          </Swap>
        </AnimatedHeight>
      </section>

{showDocuments ? <>
      <section className="kycEntityMobileSec is-documents">
        <h3 className="kycEntityMobileSecHead"><span className="kycEntityMobileNum">2</span>Required documents{documentsBadge}</h3>
        <AnimatedHeight>
          <Swap id={`${country}:${listKey}`}>
            {general && <p className="kycEntityGuideGeneral">{entityGeneralGuidance(docTypes)}</p>}
            {loading && <p className="kycEntityGuideLoading" role="status">
              <span className="kycEntityGuideSpinner" aria-hidden="true" /> Loading the documents usually accepted…
            </p>}
            {hintLine}
            {showList && listGuide.required.length > 0 && <>
              <p className="kycEntityMobileRule">{mobileRule}</p>
              <MobileDocumentRows docs={listGuide.required} checklist={checklist} ticked={ticked} />
            </>}
            {showList && listGuide.optional.length > 0 && <div className="kycEntityMobileOptional">
              <button type="button" className="kycEntityMobileOptionalToggle" aria-expanded={optionalOpen}
                onClick={() => setOptionalOpen(open => !open)}>
                <span>Optional ({listGuide.optional.length})</span>
                <i className={`bi bi-chevron-${optionalOpen ? 'up' : 'down'}`} aria-hidden="true" />
              </button>
              {optionalOpen && <div className="kycEntityMobileOptionalBody">
                <p className="kycEntityGuideOptionalHint">{OPTIONAL_DOCUMENTS_HINT}</p>
                <MobileDocumentRows docs={listGuide.optional} checklist={false} />
              </div>}
            </div>}
          </Swap>
        </AnimatedHeight>
      </section>

      <p className="kycEntityGuideHint"><i className="bi bi-info-circle" aria-hidden="true" /> Have a different official document? Upload it and we'll review it.</p>
      {uploadSlot}
      </> : documentsNote && <p className="kycEntityTypeNote" role="note"><i className="bi bi-info-circle" aria-hidden="true" /> {documentsNote}</p>}

      <BottomSheet open={choosingType} title="Which describes your company?" onClose={() => setChoosingType(false)}>
        <div role="radiogroup" aria-label="Company type">
          {[...guide.cases.map((kase, index) => [index, kase.name || `Option ${index + 1}`]), ['other', 'Not listed / not sure']].map(([value, label]) =>
            <button key={value} type="button" role="radio" aria-checked={selected === value}
              className={`kycEntitySheetOption${selected === value ? ' is-selected' : ''}`}
              onClick={() => { setSelected(value); setOptionalOpen(false); setChoosingType(false); }}>
              <span className="kycEntitySheetRadio" aria-hidden="true" />{label}
              {hinted.includes(value) && <em className="kycEntitySheetSuggested">Suggested</em>}
            </button>)}
        </div>
      </BottomSheet>
      <MobileCountrySheet open={changingCountry} value={country} title="Country of registration" onClose={() => setChangingCountry(false)}
        onChoose={value => { if (value !== country) onCountryChange(value); setChangingCountry(false); }} />
    </div>;
  }

  // Desktop: two cards stacked beside the upload card, the organisation (type, country, company type)
  // and the documents usually accepted.
  const allTypesList = showTypes && !Number.isInteger(selected) && !(narrowed && hinted.length === 1);
  return <div className="kycEntityGuideStack">
    <div className="kycEntityGuide is-identity">
      {organisationHead}
      {showDocuments && <AnimatedHeight>
        <Swap id={`${country}:${loading ? 'loading' : 'ready'}`}>
          {showTypes && <div className="kycEntityGuideTypes">
            <p className="kycEntityGuideLabel">Which describes your company? <span>Optional</span></p>
            <div className="kycEntityGuideChips" role="radiogroup" aria-label="Company type">
              {guide.cases.map((kase, index) => <button key={kase.name || index} type="button" role="radio"
                aria-checked={selected === index} className={`kycEntityGuideChip${selected === index ? ' is-selected' : ''}${hinted.includes(index) ? ' is-suggested' : ''}`}
                onClick={() => setSelected(selected === index ? null : index)}>{kase.name || `Option ${index + 1}`}</button>)}
              <button type="button" role="radio" aria-checked={selected === 'other'}
                className={`kycEntityGuideChip is-other${selected === 'other' ? ' is-selected' : ''}`}
                onClick={() => setSelected(selected === 'other' ? null : 'other')}>Not listed / not sure</button>
            </div>
          </div>}
        </Swap>
      </AnimatedHeight>}
    </div>

    {showDocuments ? <div className="kycEntityGuide is-documents">
      <p className="kycEntityGuideTitle">Usually accepted documents</p>
      <AnimatedHeight>
        <Swap id={`${country}:${listKey}`}>
          {general && <p className="kycEntityGuideGeneral">{entityGeneralGuidance(docTypes)}</p>}
          {hintLine}

          {loading && <p className="kycEntityGuideLoading" role="status">
            <span className="kycEntityGuideSpinner" aria-hidden="true" /> Loading the documents usually accepted…
          </p>}

          {listGuide.required.length > 0 && !forCountry?.fallback && <div className="kycEntityGuideDocs">
            {/* All company types listed: the card title says it all. */}
            {!allTypesList && <p className="kycEntityGuideLabel">{listGuide.rule}</p>}
            <DocumentRows docs={listGuide.required} compact={allTypesList} ticked={ticked} />
          </div>}

          {listGuide.optional.length > 0 && !forCountry?.fallback && <div className="kycEntityGuideDocs is-optional">
            <p className="kycEntityGuideLabel">Optional</p>
            <p className="kycEntityGuideOptionalHint">{OPTIONAL_DOCUMENTS_HINT}</p>
            <DocumentRows docs={listGuide.optional} compact={allTypesList} ticked={ticked} />
          </div>}
        </Swap>
      </AnimatedHeight>
      <p className="kycEntityGuideHint"><i className="bi bi-info-circle" aria-hidden="true" /> Have a different official document? Upload it and we'll review it.</p>
    </div> : documentsNote && <p className="kycEntityTypeNote" role="note"><i className="bi bi-info-circle" aria-hidden="true" /> {documentsNote}</p>}
  </div>;
}
